import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Filter, RefreshCw, ChevronLeft, ChevronRight, ArrowLeft,
  Briefcase, GraduationCap, Users, LogIn, LogOut, AlertTriangle, Clock,
  Download, FileText, Building2,
} from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";

const eventBadge = {
  drop_off: { label: "Drop Off", Icon: LogIn, cls: "bg-emerald-100 text-emerald-800" },
  pickup: { label: "Pickup", Icon: LogOut, cls: "bg-blue-100 text-blue-800" },
  tardy: { label: "Tardy", Icon: Clock, cls: "bg-amber-100 text-amber-800" },
  early_dismissal: { label: "Early Dismissal", Icon: AlertTriangle, cls: "bg-orange-100 text-orange-800" },
  visitor: { label: "Visitor", Icon: Users, cls: "bg-slate-100 text-slate-700" },
  contractor: { label: "Contractor", Icon: Users, cls: "bg-violet-100 text-violet-800" },
};

const PAGE_SIZE = 50;

export default function CorporateAttendancePage() {
  const nav = useNavigate();
  const [filters, setFilters] = useState({
    location_id: "",
    kind: "all",
    visitor_type: "",
    event_type: "",
    q: "",
    from: "",
    to: "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);

  const buildParams = useCallback((extra = {}) => {
    const p = {
      location_id: filters.location_id || undefined,
      kind: filters.kind !== "all" ? filters.kind : undefined,
      visitor_type: filters.visitor_type || undefined,
      event_type: filters.event_type || undefined,
      q: filters.q || undefined,
      from: filters.from ? new Date(filters.from + "T00:00:00Z").toISOString() : undefined,
      to: filters.to ? new Date(filters.to + "T23:59:59Z").toISOString() : undefined,
      ...extra,
    };
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/corporate/attendance", {
        params: buildParams({ limit: PAGE_SIZE, offset }),
      });
      setData(data);
    } catch (e) {
      if (e.response?.status === 403 || e.response?.status === 404) {
        toast.error("Corporate access required.");
        nav("/admin");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
      }
    } finally {
      setLoading(false);
    }
  }, [buildParams, offset, nav]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => { setOffset(0); setFilters((f) => ({ ...f, [k]: e.target.value })); };

  const downloadExport = async (fmt) => {
    try {
      const token = localStorage.getItem("vlog_token");
      const params = new URLSearchParams();
      const p = buildParams();
      Object.entries(p).forEach(([k, v]) => { if (v !== undefined && v !== "") params.append(k, v); });
      const url = `${API_BASE}/corporate/attendance/export.${fmt}?${params.toString()}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error(`Export failed (${resp.status})`);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `corporate-attendance-${stamp}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
  };

  const locations = data?.locations || [];
  const rows = data?.rows || [];
  const counts = data?.counts || { total: 0, visits: 0, students: 0 };
  const pageStart = rows.length === 0 ? 0 : offset + 1;
  const pageEnd = offset + rows.length;
  const hasNext = offset + rows.length < counts.total;

  const hasSchool = useMemo(() => locations.some((l) => ["school", "daycare"].includes(l.type)), [locations]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="corporate-attendance-page">
      <header className="bg-white border-b border-slate-200 px-6 md:px-10 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <button onClick={() => nav("/corporate")} data-testid="corporate-attendance-back" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-2">
            <ArrowLeft className="h-4 w-4" /> Back to corporate console
          </button>
          <div className="text-xs uppercase tracking-[0.3em] text-slate-400 font-semibold">
            <span className="text-primary">Logerd</span> · Corporate
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mt-0.5">
            {data?.corporate_name ? `${data.corporate_name} — Cross-location attendance` : "Cross-location attendance"}
          </h1>
          <p className="text-sm text-slate-600 mt-1">Every visit and student event from every campus and office, in one place.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={load} disabled={loading} data-testid="corporate-attendance-refresh" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 transition">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button onClick={() => downloadExport("csv")} data-testid="corporate-attendance-export-csv" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 transition">
            <Download className="h-4 w-4" /> CSV
          </button>
          <button onClick={() => downloadExport("pdf")} data-testid="corporate-attendance-export-pdf" className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:shadow transition">
            <FileText className="h-4 w-4" /> PDF
          </button>
        </div>
      </header>

      <main className="px-6 md:px-10 py-8 space-y-6">
        {/* Stat tiles */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="corporate-attendance-stats">
          <Tile label="Total events" value={counts.total} Icon={Users} />
          <Tile label="Visitor / contractor" value={counts.visits} Icon={Briefcase} accent="primary" />
          <Tile label="Student events" value={counts.students} Icon={GraduationCap} accent="emerald" />
          <Tile label="Locations" value={locations.length} Icon={Building2} accent="amber" />
        </section>

        {/* Filters */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="corporate-attendance-filters">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-4">
            <Filter className="h-3.5 w-3.5" /> Filters
          </div>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input value={filters.q} onChange={set("q")} data-testid="corp-att-search" placeholder="Search name, host, picker…" className="w-full h-10 rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
            </div>
            <select value={filters.location_id} onChange={set("location_id")} data-testid="corp-att-location" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none">
              <option value="">All locations</option>
              {locations.map((l) => (
                <option key={l.id} value={l.id}>{l.name}{l.type === "school" ? " (school)" : " (office)"}</option>
              ))}
            </select>
            <select value={filters.kind} onChange={set("kind")} data-testid="corp-att-kind" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none">
              <option value="all">All kinds</option>
              <option value="visit">Visitors / Contractors</option>
              {hasSchool && <option value="student">Student events</option>}
            </select>
            <input type="date" value={filters.from} onChange={set("from")} data-testid="corp-att-from" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
            <input type="date" value={filters.to} onChange={set("to")} data-testid="corp-att-to" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
          </div>
          {(filters.kind === "visit" || filters.kind === "all") && (
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3 mt-3">
              <select value={filters.visitor_type} onChange={set("visitor_type")} data-testid="corp-att-visitor-type" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none">
                <option value="">All visitor types</option>
                <option value="visitor">Visitor</option>
                <option value="contractor">Contractor</option>
              </select>
              {hasSchool && (filters.kind === "student" || filters.kind === "all") && (
                <select value={filters.event_type} onChange={set("event_type")} data-testid="corp-att-event-type" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none">
                  <option value="">All student events</option>
                  <option value="drop_off">Drop off</option>
                  <option value="pickup">Pickup</option>
                  <option value="tardy">Tardy</option>
                  <option value="early_dismissal">Early dismissal</option>
                </select>
              )}
            </div>
          )}
        </section>

        {/* Table */}
        <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid="corporate-attendance-table-section">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="corporate-attendance-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                  <th className="px-5 py-3.5">When</th>
                  <th className="px-5 py-3.5">Location</th>
                  <th className="px-5 py-3.5">Event</th>
                  <th className="px-5 py-3.5">Name</th>
                  <th className="px-5 py-3.5">With / Host</th>
                  <th className="px-5 py-3.5">Detail</th>
                  <th className="px-5 py-3.5">Officer</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !loading && (
                  <tr><td colSpan={7} className="px-5 py-14 text-center text-slate-500">No events match the selected filters.</td></tr>
                )}
                {loading && rows.length === 0 && (
                  <tr><td colSpan={7} className="px-5 py-14 text-center text-slate-500">Loading…</td></tr>
                )}
                {rows.map((r) => {
                  const cfg = r.kind === "student"
                    ? (eventBadge[r.subtype] || eventBadge.drop_off)
                    : (eventBadge[r.subtype] || eventBadge.visitor);
                  const isSchool = ["school", "daycare"].includes(r.location_type);
                  return (
                    <tr key={`${r.kind}-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`corp-att-row-${r.id}`}>
                      <td className="px-5 py-3 text-slate-700 whitespace-nowrap text-xs">{new Date(r.occurred_at).toLocaleString()}</td>
                      <td className="px-5 py-3">
                        <div className="font-medium text-slate-900">{r.location_name}</div>
                        <span className={`inline-flex items-center gap-1 mt-0.5 text-[10px] font-semibold uppercase tracking-wider ${isSchool ? "text-blue-700" : "text-slate-500"}`}>
                          {isSchool ? <GraduationCap className="h-3 w-3" /> : <Briefcase className="h-3 w-3" />}
                          {isSchool ? "School" : r.location_type === "care" ? "Care home" : "Office"}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${cfg.cls}`}>
                          <cfg.Icon className="h-3 w-3" /> {cfg.label}
                        </span>
                      </td>
                      <td className="px-5 py-3 font-medium text-slate-900">{r.name}</td>
                      <td className="px-5 py-3 text-slate-700">{r.secondary || "—"}</td>
                      <td className="px-5 py-3 text-slate-600">
                        {r.kind === "visit" ? (
                          <>
                            <div className="text-slate-700">{r.purpose || "—"}</div>
                            {r.company && <div className="text-xs text-slate-500">{r.company}</div>}
                          </>
                        ) : (
                          <>
                            {r.grade && <div className="text-xs uppercase tracking-wider text-slate-500">Grade</div>}
                            <div className="text-slate-700">{r.grade || "—"}</div>
                          </>
                        )}
                      </td>
                      <td className="px-5 py-3 text-slate-700">{r.officer_name || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-600">
            <div data-testid="corp-att-pagination-info">
              Showing <b>{pageStart}</b>–<b>{pageEnd}</b> of <b>{counts.total}</b>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0 || loading}
                data-testid="corp-att-prev-page"
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-100 disabled:opacity-40 transition"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </button>
              <button
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={!hasNext || loading}
                data-testid="corp-att-next-page"
                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-100 disabled:opacity-40 transition"
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function Tile({ Icon, label, value, accent }) {
  const ring = {
    primary: "ring-primary/20 bg-primary/5",
    amber: "ring-amber-200 bg-amber-50",
    emerald: "ring-emerald-200 bg-emerald-50",
  }[accent] || "ring-slate-200 bg-white";
  return (
    <div className={`rounded-2xl border border-slate-200 p-5 ${ring} ring-1`}>
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <Icon className="h-5 w-5 text-slate-400" />
      </div>
      <div className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900">{value}</div>
    </div>
  );
}
